import { updateProperty } from '@go-client/lib/api/property';
import { notUndefined } from '@go-shared/lib/functional';
import { BareProperty, PropertyReservablesConfig } from '@go-shared/types/property';
import { ObjectId } from 'bson';
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Editor } from '..';
import {
  BubbleButton,
  BubbleFormGroup,
  BubbleImageSelector,
  BubbleCurrency,
  BubblePercentage,
  BubbleInput,
} from '../BubbleUI';
import StripeOnboarding from '../StripeOnboarding';

interface PropertyProfileFormDataBase {
  config: PropertyReservablesConfig;
}

type PropertyProfileFormData = PropertyProfileFormDataBase & {
  _dirty: boolean;
  _errors: Partial<PropertyProfileFormErrors>;
};

type PropertyProfileFormErrors = { [K in keyof PropertyProfileFormDataBase]: string };

function DEFAULT_FORM_DATA(property?: BareProperty): PropertyProfileFormData {
  return {
    config: { ...(property?.reservablesConfig ?? {}) },
    _dirty: false,
    _errors: {},
  };
}

interface PropertyProfileFormProps {
  edit: BareProperty;
  actionsElem?: HTMLDivElement;
  onFormSubmitted?: () => Promise<void>;
}

export default function PropertyProfileForm(props: PropertyProfileFormProps) {
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<PropertyProfileFormData>(DEFAULT_FORM_DATA(props.edit));

  useEffect(() => {
    resetForm();
  }, [props.edit]);

  function resetForm() {
    setFormData(DEFAULT_FORM_DATA(props.edit));
  }

  function changeForm<T extends keyof PropertyReservablesConfig>(
    key: T,
    val: PropertyReservablesConfig[T]
  ) {
    setFormData({ ...formData, _dirty: true, config: { ...formData.config, [key]: val } });
  }

  async function submitForm() {
    setSaving(true);

    try {
      await updateProperty(props.edit._id, {
        ...props.edit,
        reservablesConfig: {
          ...(props.edit.reservablesConfig || {}),
          ...formData.config,
        },
      });

      await props.onFormSubmitted?.();
    } finally {
      setSaving(false);
    }
  }

  const errored = Object.keys(formData._errors).length > 0;

  const formHtml = (
    <div className="grid grid-cols-3 gap-4">
      <BubbleFormGroup label="Avatar" className="col-span-3">
        <BubbleImageSelector
          maxItems={1}
          value={[formData.config.avatar].filter(notUndefined)}
          onValue={images => changeForm('avatar', images[0])}
        />
      </BubbleFormGroup>
      <BubbleFormGroup label="Stripe Account" className="col-span-3">
        <StripeOnboarding
          identifier={props.edit._id}
          mode="property"
          stripeAccountId={props.edit.reservablesConfig?.stripeAccountId}
          hasCompletedOnboarding={!!props.edit.reservablesConfig?.hasCompletedOnboarding}
        />
      </BubbleFormGroup>
      <div className="col-span-3 grid grid-cols-2 gap-4">
        <BubbleFormGroup label="Service Fee Percentage">
          <div className="flex items-start">
            <BubblePercentage
              value={formData.config.serviceFeePercentage}
              onValue={val => changeForm('serviceFeePercentage', val)}
              block
              className="flex-grow"
            />
          </div>
          <div className="text-slate italic mt-1">
            Charged to guests. 4% is Spark's platform fee.
          </div>
        </BubbleFormGroup>
        <BubbleFormGroup label="Service Fee Flat">
          <div className="flex items-start">
            <BubbleCurrency
              min={0}
              value={formData.config.serviceFeeFlatAmountCents}
              onValue={val => changeForm('serviceFeeFlatAmountCents', val)}
              block
              className="flex-grow"
            />
          </div>
          <div className="text-slate italic mt-1">
            Charged to guests. $0.30 is Spark's platform fee.
          </div>
        </BubbleFormGroup>
        <BubbleFormGroup label="Backup Postal Code">
          <div className="flex items-start">
            <BubbleInput
              type="text"
              value={formData.config.backupPostalCode}
              onValue={val => changeForm('backupPostalCode', val)}
              block
              className="flex-grow"
            />
          </div>
          <div className="text-slate italic mt-1">
            Used to calculate taxes when address is not available in Stripe.
          </div>
        </BubbleFormGroup>
      </div>
      <BubbleFormGroup label="Confirmation Email Footer" className="col-span-3">
        <Editor
          textareaName="reservables.activityLocation"
          value={formData.config.confirmationEmailFooter || ''}
          onValue={val => changeForm('confirmationEmailFooter', val)}
          mode="text-link"
        />
      </BubbleFormGroup>
      <div className="col-span-3">
        <div className="font-semibold mb-2">Terminal Registry</div>
        {(formData.config.terminalRegistrations ?? []).map((terminal, i) => {
          const registrations = formData.config.terminalRegistrations ?? [];
          return (
            <div key={terminal._id} className="grid grid-cols-2 gap-4 items-end mb-2">
              <BubbleFormGroup label="Label">
                <BubbleInput
                  type="text"
                  block
                  value={terminal.label}
                  onValue={val => {
                    const next = [...registrations];
                    next[i] = { ...next[i], label: val };
                    changeForm('terminalRegistrations', next);
                  }}
                />
              </BubbleFormGroup>
              <div className="flex items-end gap-2">
                <BubbleFormGroup label="Reader ID" className="flex-grow">
                  <BubbleInput
                    type="text"
                    block
                    value={terminal.readerId}
                    onValue={val => {
                      const next = [...registrations];
                      next[i] = { ...next[i], readerId: val };
                      changeForm('terminalRegistrations', next);
                    }}
                  />
                </BubbleFormGroup>
                <BubbleButton
                  variant="crimson"
                  inverse
                  icon="trash-alt"
                  onClick={() => {
                    const next = [...registrations];
                    next.splice(i, 1);
                    changeForm('terminalRegistrations', next);
                  }}
                />
              </div>
            </div>
          );
        })}
        <BubbleButton
          variant="navy"
          inverse
          icon="plus"
          onClick={() => {
            const next = [
              ...(formData.config.terminalRegistrations ?? []),
              { _id: new ObjectId().toString(), label: '', readerId: '' },
            ];
            changeForm('terminalRegistrations', next);
          }}
        >
          Add Terminal
        </BubbleButton>
      </div>
    </div>
  );

  const formActions = (
    <div className="w-full flex items-center">
      <div className="ml-auto" />
      <BubbleButton
        variant="navy"
        onClick={resetForm}
        inverse
        disabled={!formData._dirty || errored || saving}
        icon="rotate-left"
      >
        Reset
      </BubbleButton>
      <BubbleButton
        variant="navy"
        onClick={submitForm}
        disabled={!formData._dirty || errored || saving}
        icon="floppy-disk"
      >
        Save Changes
      </BubbleButton>
    </div>
  );

  const portalButtons = props.actionsElem ? createPortal(formActions, props.actionsElem) : <></>;

  return (
    <>
      {formHtml}
      {portalButtons}
    </>
  );
}
